import { useWindowDimensions } from 'react-native';

const ORIENTATION_THRESHOLD = 1.05; // Prevents flip-flopping on near-square screens

export type Orientation = 'portrait' | 'landscape' | 'square';
export type SizeClass = 'compact' | 'regular' | 'wide';

export interface ScreenInfo {
    width: number;
    height: number;
    aspect: number;
    orientation: Orientation;
    isLandscape: boolean;
    isPortrait: boolean;
    sizeClass: SizeClass;
    scale: number;
    fontScale: number;
}

/**
 * Idiomatic hook for screen orientation and size detection.
 *
 * Provides:
 * - Orientation detection with aspect ratio threshold (prevents jitter)
 * - Size classes for responsive layouts (compact/regular/wide)
 * - Works correctly in split-screen, iPad multitasking, foldables
 *
 * @example
 * const { isLandscape, sizeClass } = useScreenInfo();
 * const sheetHeight = isLandscape ? '95%' : '85%';
 */
export function useScreenInfo(): ScreenInfo {
    const { width, height, scale, fontScale } = useWindowDimensions();
    const w = Math.round(width);
    const h = Math.round(height);
    const aspect = w / h;

    const orientation: Orientation =
        aspect > ORIENTATION_THRESHOLD
            ? 'landscape'
            : aspect < 1 / ORIENTATION_THRESHOLD
              ? 'portrait'
              : 'square';

    // Width-based breakpoints (more robust than orientation alone)
    const sizeClass: SizeClass = w >= 900 ? 'wide' : w >= 600 ? 'regular' : 'compact';

    return {
        width: w,
        height: h,
        aspect,
        orientation,
        isLandscape: orientation === 'landscape',
        isPortrait: orientation === 'portrait',
        sizeClass,
        scale,
        fontScale,
    };
}
